import { IHeader, IMyAccountLogin } from '@/.generated';
import { desktopHeaderVariants } from '../Header.styles';
import { NavigationLinkWrapper } from '@/helpers/Wrappers/NavigationLinkWrapper/NavigationLinkWrapper';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';
import { useGlobalLabels } from '@/context/GlobalLabelContext';
import { HeaderLoginSection } from './HeaderLoginSection';
import { HeaderFeaturedSection } from '../HeaderFeaturedSection';
import { HeaderIdentifierLegend } from './HeaderIdentifierLegend';
import { visibleNavLinks } from '../visibleNavLinks';

// Element types resolved from the IHeader nav tree. In V2 the deeper levels are
// nested per-link: each L2 link owns its L3 groups. The header renders up to L3 only.
type NavColumn = NonNullable<
  NonNullable<IHeader['nav_level_one']>[number]['nav_columns']
>[number];
type L2Item = NonNullable<NavColumn['nav_level_two']>[number];
type L2Link = NonNullable<L2Item['navigation_level_two_links']>[number];
type L3Item = NonNullable<L2Link['navigation_level_three']>[number];

type NavLevelOneItem = NonNullable<IHeader['nav_level_one']>[number] & {
  activeMenuIndex: number | null;
  currentIndex: number;
  myAccountLoginReference?: IMyAccountLogin;
};

// Render a level-three group: its title and its links (deepest level shown in the header).
const LevelThreeGroup = ({
  l3Item,
  styles,
}: {
  l3Item: L3Item;
  styles: ReturnType<typeof desktopHeaderVariants>;
}) => {
  const { l3Title, menuLink } = styles;
  const visibleL3Links = visibleNavLinks(l3Item.navigation_level_three_links);
  if (!visibleL3Links.length) return null;

  return (
    <div>
      <PlainTextWrapper
        content={l3Item.navigation_level_three_title}
        className={l3Title()}
        cslpAttribute={l3Item.$?.navigation_level_three_title}
      />
      <ul>
        {visibleL3Links.map((link, index) => (
          <li key={index}>
            <NavigationLinkWrapper
              navigationLink={link}
              clickLocation="headerNav"
              siteSection={l3Item.navigation_level_three_title}
              className={menuLink()}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

// Render a level-two group: its title, its links, and any level-three groups
// nested under each of those links.
const LevelTwoGroup = ({
  l2Item,
  styles,
}: {
  l2Item: L2Item;
  styles: ReturnType<typeof desktopHeaderVariants>;
}) => {
  const { megaMenuColumnGroup, menuLink, l2Title } = styles;
  const visibleL2Links = visibleNavLinks(l2Item.navigation_level_two_links);

  if (!visibleL2Links.length) return null;

  return (
    <div className={megaMenuColumnGroup()}>
      <PlainTextWrapper
        content={l2Item.nav_level_two_title}
        className={l2Title()}
        cslpAttribute={l2Item.$?.nav_level_two_title}
      />
      <ul className={megaMenuColumnGroup()}>
        {visibleL2Links.map((link, index) => (
          <li key={index}>
            <NavigationLinkWrapper
              navigationLink={link}
              clickLocation="headerNav"
              siteSection={l2Item.nav_level_two_title}
              className={menuLink()}
            />
            {!!link.navigation_level_three?.length &&
              link.navigation_level_three.map((l3Item, l3Index) => (
                <LevelThreeGroup key={l3Index} l3Item={l3Item} styles={styles} />
              ))}
          </li>
        ))}
      </ul>
    </div>
  );
};

const NavColumn = ({
  column,
  styles,
}: {
  column: NavColumn;
  styles: ReturnType<typeof desktopHeaderVariants>;
}) => {
  const { megaMenuColumn, megaMenuColumnGroup, menuLink } = styles;

  const visibleL1Links = visibleNavLinks(column.nav_level_one_links);
  const hasLevelTwo = !!column.nav_level_two?.length;

  return (
    <div className={megaMenuColumn()}>
      {visibleL1Links.length > 0 && (
        <ul className={megaMenuColumnGroup()}>
          {visibleL1Links.map((link, index) => (
            <li key={index}>
              <NavigationLinkWrapper
                navigationLink={link}
                clickLocation="headerNav"
                siteSection="Top Links"
                className={menuLink()}
              />
            </li>
          ))}
        </ul>
      )}
      {hasLevelTwo &&
        column.nav_level_two!.map((l2Item, index) => (
          <LevelTwoGroup key={index} l2Item={l2Item} styles={styles} />
        ))}
    </div>
  );
};

export const HeaderNavigation = (props: NavLevelOneItem) => {
  const {
    featured_section,
    show_account_login_section,
    myAccountLoginReference,
    nav_columns,
    currentIndex,
    activeMenuIndex,
  } = props;
  const { globalLabels } = useGlobalLabels();

  const styles = desktopHeaderVariants({ menuVisible: activeMenuIndex === currentIndex });
  const { megaMenu, megaMenuContainer, megaMenuGrid } = styles;

  const hasNavColumns = !!nav_columns?.length;

  return (
    <div data-nav-menu className={megaMenu()}>
      <div className={megaMenuContainer()}>
        <HeaderLoginSection
          myAccountLogin={myAccountLoginReference}
          showAccountLogin={show_account_login_section}
        />
        <div className={megaMenuGrid()}>
          {hasNavColumns &&
            nav_columns!.map((column, index) => (
              <NavColumn key={index} column={column} styles={styles} />
            ))}
          <HeaderFeaturedSection featuredSection={featured_section} className="col-start-3" />
        </div>
        <HeaderIdentifierLegend globalLabels={globalLabels} />
      </div>
    </div>
  );
};
